import { KeyValuePipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed, DestroyRef, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ChildTabBarComponent } from '@app/components/child-tab-bar/child-tab-bar.component';
import { ChildTabComponent } from '@app/components/child-tab-bar/child-tab/child-tab.component';
import { EmptyStateComponent } from '@app/components/empty-state/empty-state.component';
import { ButtonComponent, CardComponent } from '@app/components/platform';
import { SegmentHeaderComponent } from '@app/components/segment-header/segment-header.component';
import { RL1Summary } from '@app/models/payroll/rl1-summary.model';
import { Rl1 } from '@app/models/payroll/rl1.model';
import { T4Summary } from '@app/models/payroll/t4-summary.model';
import { T4 } from '@app/models/payroll/t4.model';
import { TaxForm } from '@app/models/payroll/tax-form.abstract.model';
import {
    EmployeeTaxFormsTable,
    Event,
} from '@app/modules/payroll/services/tables/employee-tax-forms-table/employee-tax-forms.table';
import { EmployeeRL1Table } from '@app/modules/payroll/services/tables/employee-tax-forms-table/rl1-tax-forms.table';
import { EmployeeT4Table } from '@app/modules/payroll/services/tables/employee-tax-forms-table/t4-tax-forms.table';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { TableModule } from '@app/modules/table/table.module';
import { AppDatePipe } from '@app/pipes';
import { AuthService } from '@app/services';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';

type TaxReturnKey = 'T4' | 'RL1';
@Component({
    selector: 'app-tax-return-forms-tables',
    templateUrl: './tax-return-forms-tables.template.html',
    styleUrl: './tax-return-forms-tables.style.scss',
    standalone: true,
    imports: [
        TranslateModule,
        CardComponent,
        TableModule,
        SegmentHeaderComponent,
        ButtonComponent,
        ChildTabBarComponent,
        ChildTabComponent,
        KeyValuePipe,
        EmptyStateComponent,
        MatMenuModule,
        MatIconModule,
        NgTemplateOutlet,
        MatTooltipModule,
    ],
})
export class TaxReturnFormsTables {
    t4Summary = input<T4Summary>();
    rl1Summary = input<RL1Summary>();

    employeeTaxFormsTables = computed(() => {
        const t4Summary = this.t4Summary();
        const rl1Summary = this.rl1Summary();
        // The component always contains at least a T4 table, but it might not contain an RL1 table if that summary wasn't provided
        let tables: { T4: EmployeeT4Table; RL1?: EmployeeRL1Table };
        if (t4Summary) {
            tables = { T4: new EmployeeT4Table(this.authService, this.datePipe, t4Summary) };
            if (t4Summary.isAmendment) {
                this.subscribeToEmployeeTaxFormsTableChanges(tables.T4);
            }

            if (rl1Summary) {
                tables.RL1 = new EmployeeRL1Table(this.authService, this.datePipe, rl1Summary);
                if (rl1Summary.isAmendment) {
                    this.subscribeToEmployeeTaxFormsTableChanges(tables.RL1);
                }
            }
            return tables;
        }
    });

    /**
     * Tax packages than can have previews generated
     */
    previewableTaxPackages = computed(() => {
        this._refreshButtons();
        const previewablePackages: TaxReturnKey[] = [];
        if (this.t4Summary()?.id) {
            previewablePackages.push('T4');
        }
        if (this.rl1Summary()?.id) {
            previewablePackages.push('RL1');
        }
        return previewablePackages;
    });

    /**
     * Tax packages that are ready for emailing to employees
     */
    sendableTaxPackages = computed(() => {
        this._refreshButtons();
        const sendableTaxPackages: TaxReturnKey[] = [];
        // If summaries are submitted we show the menu items for sending emails to employees
        if (this.t4Summary()?.isSubmitted) {
            sendableTaxPackages.push('T4');
        }
        if (this.rl1Summary()?.isSubmitted) {
            // However, RL-1s have an additional check in the template to see if they are sendable, if not they will be disabled with a tooltip
            sendableTaxPackages.push('RL1');
        }
        return sendableTaxPackages;
    });

    // These are currently all outputs, but in the future much of this logic could likely be moved directly into this component once the dependant components are made standalone
    downloadPreview = output<TaxReturnKey[]>();
    addAmendedTaxForm = output();
    sendFormsToEmployees = output<TaxReturnKey[]>();
    employeeTaxFormEvent = output<MenuColumnEvent<TaxForm, Event>>();
    /**
     * A map of employee IDs and which amendment type they've already been added to
     */
    employeesInAmendment: { [employeeId: string]: { T4?: boolean; RL1?: boolean } } = {};

    get currentTaxFormsTab(): TaxReturnKey {
        return this._currentTaxFormsTab;
    }
    set currentTaxFormsTab(currentTaxFormsTab: string) {
        this._currentTaxFormsTab = currentTaxFormsTab as TaxReturnKey;
    }
    private _currentTaxFormsTab: TaxReturnKey = 'T4';
    /**
     * Dummy signal used to force re-evaluation of computed signals
     */
    private _refreshButtons = signal<boolean>(false);

    constructor(
        private authService: AuthService,
        private datePipe: AppDatePipe,
        private destroyRef: DestroyRef
    ) {}

    /**
     * Reloads the table
     * @param table T4 | RL1. If no table is provided, all tables will be reloaded
     */
    reloadTables(table?: TaxReturnKey): void {
        // This hacky approach is required to ensure we re-run the computed signals for the buttons
        // Unfortunately on our current version of Angular it is not detecting changes when we update properties of signals
        // By toggling this dummy boolean we force Angular to recalc the computed signals
        this._refreshButtons.update((c) => !c);

        const employeeTaxFormsTables = this.employeeTaxFormsTables();
        if (employeeTaxFormsTables) {
            if (table) {
                employeeTaxFormsTables[table]?.reload();
                return;
            }
            employeeTaxFormsTables.T4.reload();
            employeeTaxFormsTables.RL1?.reload();
        }
    }

    /**
     * When employees are added/removed from the amendment, ensure they're filtered from the employee auto-complete
     */
    private subscribeToEmployeeTaxFormsTableChanges(table: EmployeeTaxFormsTable<T4 | Rl1>): void {
        table.onLoading
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((isLoading) => !isLoading)
            )
            .subscribe(() =>
                table.paginator.values.forEach((taxForm) => {
                    // Add the employee to the relevant amendment type (T4 or RL-1)
                    const existingEmployeeForms = this.employeesInAmendment[taxForm.employee.id.toString()] ?? {};
                    this.employeesInAmendment[taxForm.employee.id.toString()] = {
                        ...existingEmployeeForms,
                        [table.type]: true,
                    };
                })
            );
    }
}
